import { useContext } from 'react';

import { placeholderToLabel } from '@configs/FILTERS_PLACEHOLDERS';

import { getDisplayValues, getPrimaryDisplayValue, groupConversionValues } from '@libs/NwFeatures';
import { getMoment, showDate, showDateNoTime, toISODate } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useFeaturesIndex } from '@hooks/API/UseFeatures';
import { useFolders } from '@hooks/API/UseFolders';
import useNetworkAgencyDepartments from '@hooks/CachedLists/UseNetworkAgencyDepartments';
import useDataPlaceholder from '@hooks/Utils/UseDataPlaceholder';

import { useContactsSelector } from '@components/ContactsSelector/Common/ContactsSelectorContext';
import { findFeature, findFilterInFiltersBySection } from '@components/Filters/FilterForm/FilterUtils';

/**
 * Traduce un filtro attivo in etichetta + valori leggibili.
 * Usato sia dalla lista dei filtri selezionati che dal riepilogo compatto su mobile,
 * così le due viste non divergono.
 *
 * Con `firstMetricOnly` le conversion mostrano solo la prima metrica configurata
 * dall'utente invece di tutte: serve al riepilogo compatto, dove lo spazio è poco.
 *
 * Ritorna `(filter) => { label, values: string[] } | null`.
 * È null quando il filtro non è nella configurazione o quando è la seconda metà di una
 * coppia già rappresentata dalla prima (AgeUntil dopo AgeSince, AddedUntil dopo AddedSince).
 */
const useSelectedFilterDisplay = ({ firstMetricOnly = false } = {}) => {
    const { cachedList } = useContext(ListContext);

    const { global, selection } = useContactsSelector();
    const { filters } = selection;
    const { featuresList, filtersBySection } = global;

    const featuresIndex = useFeaturesIndex();
    const replacePlaceHolder = useDataPlaceholder();

    const { data: folders } = useFolders();
    const getFolderNames = (folderIds) => {
        const names = folderIds.map((folderId) => {
            const folder = folders && folders.find((item) => item.ID === folderId);
            return folder ? folder.Name : '';
        });
        return names.filter((name) => name).join(', ');
    };

    const allDepartments = useNetworkAgencyDepartments();
    const getDepartmentName = (departmentId) => {
        const dept = allDepartments.find((d) => d.ID === departmentId);
        if (dept) {
            if (!dept.SingleNetworkAgency) {
                return `${dept.NetworkAgencyName} - ${dept.Name}`;
            }
            return dept.Name;
        }
        return '';
    };

    const formatFilterValue = (type, value) => {
        switch (type) {
            case 'period':
                if (typeof value === 'string') {
                    const range = replacePlaceHolder(value);
                    const toRenderrange =
                        range[0] === range[1]
                            ? getMoment(range[0]).format('DD MMM YYYY')
                            : `${getMoment(range[0]).format('DD MMM YYYY')} - ${getMoment(range[1]).format('DD MMM YYYY')}`;
                    const match = value.match(/\{(\w+)\}/);
                    let periodstring = match ? placeholderToLabel(match[0]) : value;
                    return `${periodstring} (${toRenderrange})`;
                } else {
                    return `(${getMoment(value[0]).format('DD MMM YYYY')} - ${getMoment(value[1]).format('DD MMM YYYY')})`;
                }
            case 'date':
                if (typeof value === 'string') {
                    const match = value.match(/\{(\w+)\}/);
                    return match ? placeholderToLabel(match[0]) : value;
                } else {
                    return value ? getMoment(value).format('MMM DD YYYY') : '';
                }
            case 'booker-selector':
                const booker = cachedList.users.items.find((el) => el.ID === value);
                if (booker) {
                    return `${booker.Name} ${booker.Surname}`;
                }
                return '';
            case 'departments-selector':
            case 'departments-selector-accounting':
                if (value === -1) {
                    return 'At least one Department';
                } else if (value === -2) {
                    return 'No Departments';
                } else {
                    return getDepartmentName(value);
                }
            case 'departments-selector-multi':
                const departments = [];
                value.forEach((d) => {
                    const departmentName = getDepartmentName(d);
                    departments.push(departmentName);
                });
                return departments.length > 0 ? departments.join(', ') : 'All Departments';
            case 'model-selector':
            case 'model-selector-including-scouting':
            case 'customer-selector':
            case 'service-selector':
            case 'contact-selector':
            case 'client-selector':
            case 'agency-selector':
                return getContactName(value);
            case 'filter-yes-no-all':
                return value === true ? 'Yes' : value === false ? 'No' : 'All';
            case 'gender':
                return value;
            case 'models-tag':
            case 'customers-tag':
                const tags = [];
                const cachedtags = cachedList.publictags.items.concat(cachedList.privatetags.items);
                value.forEach((t) => {
                    const tag = cachedtags.find((el) => el.ID === t);
                    tags.push(tag.Name);
                });
                return tags.join(', ');
            case 'countries-selector':
                const countries = [];
                value.forEach((c) => {
                    const country = cachedList.countries.items.find((el) => el.Code === c);
                    countries.push(country.Name);
                });
                return countries.join(', ');
            case 'country-selector':
                const country = cachedList.countries.items.find((el) => el.Code === value);
                return country.Name;
            case 'age-range':
                if (!value[0]) {
                    return `Up to ${value[1]} years`;
                } else if (!value[1]) {
                    return `${value[0]} years and over`;
                } else {
                    return `Between ${value[0]} and ${value[1]} years`;
                }
            case 'percentage-range':
                if (!value[0]) {
                    return `Up to ${value[1]}%`;
                } else if (!value[1]) {
                    return `${value[0]}% and over`;
                } else {
                    return `Between ${value[0]}% and ${value[1]} %`;
                }
            case 'cities-selector':
                return value.join(', ');
            case 'intown-selector':
                if (value) {
                    return 'In Town';
                } else {
                    return 'Out of Town';
                }
            case 'boolean':
                return value ? 'yes' : 'no';
            case 'statement-filter':
                return `${value[0]} ${value[3] ? value[3] : 'in'} ${value[1]}`; //${value[2] ? `- ${value[2]}` : ''} need to parse label for transaction label
            default:
                return value;
        }
    };

    const getCustomFieldValue = (filterObj, filter) => {
        if (filterObj.type === 'MultipleChoice' || filterObj.type === 'Choice') {
            return filter.value.join(', ');
        }
        if (filterObj.type === 'Numeric') {
            return filter.value.join(' - ');
        }
        if (filterObj.type === 'Boolean') {
            return filter.value ? 'True' : 'False';
        }
        if (filterObj.type === 'Date') {
            if (Array.isArray(filter.value)) {
                return `${toISODate(filter.value[0])} / ${toISODate(filter.value[1])}`;
            }
            return filter.value ? showDate(filter.value) : '';
        }
        return filter.value;
    };

    const getFeatureValues = (filter) => {
        const feature = findFeature(filter.name, featuresList);
        const values = [];
        switch (feature.TypeName) {
            case 'Conversion':
                const conversion = featuresIndex.conversionById.get(feature.ConversionID);
                const conversionValues = groupConversionValues(conversion.ConversionValues);
                const stepMin = conversionValues.find((step) => step.Key === filter.value[0]);
                const stepMax = conversionValues.find((step) => step.Key === filter.value[1]);
                // il riepilogo compatto ha poco spazio: una sola metrica invece di tutte
                const displayValue = firstMetricOnly ? getPrimaryDisplayValue : getDisplayValues;
                values.push(
                    `Min: ${displayValue(stepMin.DisplayValue, conversion.ID, featuresIndex, { separator: ' - ' })}`
                );
                values.push(
                    `Max: ${displayValue(stepMax.DisplayValue, conversion.ID, featuresIndex, { separator: ' - ' })}`
                );
                break;
            case 'Numeric':
                values.push(`Min: ${filter.value[0]}`);
                values.push(`Max: ${filter.value[1]}`);
                break;
            case 'Rating':
                values.push(`at least ${filter.value && '*'.repeat(parseInt(filter.value))}`);
                break;
            case 'Multichoice':
                const multiChoices = feature.Choices.filter((choice) => filter.value.choices.includes(choice.ID));
                const multiChoicesString = multiChoices
                    .map((choice) => choice.Value)
                    .join(filter.value.operator ? (filter.value.operator === 'and' ? ' + ' : ' or ') : '+');
                values.push(`${feature.Name}:  ${multiChoicesString}`);
                break;
            case 'Choice':
                const choices = feature.Choices.filter((choice) => filter.value.includes(choice.ID));
                values.push(`${feature.Name}:  ${choices.map((choice) => choice.Value).join(', ')}`);
                break;
            case 'Boolean':
                values.push(`${feature.Name}: ${filter.value === null ? '?' : filter.value ? 'Yes' : 'No'}`);
                break;
            default:
                values.push(filter.value);
                break;
        }
        return values;
    };

    return (filter) => {
        const filterObj = filtersBySection && findFilterInFiltersBySection(filter.name, filtersBySection);
        if (!filterObj) {
            return null;
        }

        if (filterObj.source === 'customField') {
            return {
                label: filter.name.replace(/_/g, ' '),
                values: [getCustomFieldValue(filterObj, filter)],
            };
        }

        if (filterObj.source === 'feature' && featuresList) {
            return { label: filterObj.label, values: getFeatureValues(filter) };
        }

        if (filterObj.source === 'filter' && filterObj.type === 'intown-selector') {
            return { label: filter.value ? 'In Town' : 'Out of Town', values: [] };
        }

        // Le coppie da/a sono rappresentate da un solo elemento, sulla prima delle due
        if (filterObj.name === 'AgeSince') {
            const ageUntil = filters.active.find((f) => f.name === 'AgeUntil');
            if (ageUntil) {
                return { label: 'Age', values: [`${filter.value} - ${ageUntil.value}`] };
            }
            return { label: filterObj.label, values: [filter.value] };
        }
        if (filterObj.name === 'AgeUntil') {
            if (filters.active.some((f) => f.name === 'AgeSince')) {
                return null;
            }
            return { label: filterObj.label, values: [filter.value] };
        }
        if (filterObj.name === 'AddedSince') {
            const addedUntil = filters.active.find((f) => f.name === 'AddedUntil');
            if (addedUntil) {
                return {
                    label: 'Added',
                    values: [
                        `${showDateNoTime(replacePlaceHolder(filter.value), true)} - ${showDateNoTime(replacePlaceHolder(addedUntil.value), true)}`,
                    ],
                };
            }
            return { label: filterObj.label, values: [showDateNoTime(replacePlaceHolder(filter.value), true)] };
        }
        if (filterObj.name === 'AddedUntil') {
            if (filters.active.some((f) => f.name === 'AddedSince')) {
                return null;
            }
            return { label: filterObj.label, values: [showDateNoTime(replacePlaceHolder(filter.value), true)] };
        }

        // sulle folder l'array vuoto non elenca niente: vuol dire "in almeno una" / "in nessuna"
        if (filterObj.type === 'folders-selector' && Array.isArray(filter.value)) {
            if (filter.value.length === 0) {
                return {
                    label: 'Folders',
                    values: [filterObj.name === 'WithFolders' ? 'in at least one folder' : 'in no folder'],
                };
            }
            return { label: filterObj.label, values: [getFolderNames(filter.value)] };
        }

        return { label: filterObj.label, values: [formatFilterValue(filterObj.type, filter.value)] };
    };
};

export default useSelectedFilterDisplay;
